import { Injectable, inject } from '@angular/core';
import { Firestore, collection, getDocs, query, where, Timestamp } from '@angular/fire/firestore';
import { Auth, authState } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { first, filter, timeout } from 'rxjs/operators';
import { DailyMetrics, MonthlyMetrics, CoreMetrics, AnalyticsEvent } from '../shared';
import moment from 'src/app/utils/moment';
import { safeFind } from 'src/app/utils';

export interface AdminDashboardMetrics {
  daily: DailyMetrics[];
  monthly: MonthlyMetrics[];
  summary: {
    totalUsers: number;
    totalAccounts: number;
    totalRevenue: number;
    activeUsersToday: number;
    activeAccountsToday: number;
    revenueToday: number;
    revenueThisMonth: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AdminAnalyticsService {

  private firestore = inject(Firestore);
  private auth = inject(Auth);

  constructor() {}

  private async ensureAuthenticated(): Promise<void> {
    const user = await firstValueFrom(
      authState(this.auth).pipe(
        filter(u => u !== null),
        first(),
        timeout(10000)
      )
    );
    
    if (!user) {
      throw new Error('User must be authenticated to access admin metrics');
    }
  }

  async getDashboardMetrics(): Promise<AdminDashboardMetrics> {
    await this.ensureAuthenticated();
    
    const today = (moment as any)().format('YYYY-MM-DD');
    const thisMonth = (moment as any)().format('YYYY-MM');
    
    // Get latest daily metrics (last 30 days)
    const dailyMetrics = await this.getDailyMetrics(
      (moment as any)().subtract(30, 'days').format('YYYY-MM-DD'),
      today
    );

    // Get latest monthly metrics (last 12 months)
    const monthlyMetrics = await this.getMonthlyMetrics(
      (moment as any)().subtract(12, 'months').format('YYYY-MM'),
      thisMonth
    );

    // Get today's metrics for summary
    const todayMetrics = safeFind(dailyMetrics, m => m.date === today);
    const thisMonthMetrics = safeFind(monthlyMetrics, m => m.date.startsWith(thisMonth));
    
    // Get latest monthly metrics for totals
    const latestMonthly = monthlyMetrics[monthlyMetrics.length - 1];

    return {
      daily: dailyMetrics,
      monthly: monthlyMetrics,
      summary: {
        totalUsers: latestMonthly?.metrics?.activeUsers || 0,
        totalAccounts: latestMonthly?.metrics?.activeOrganizations || 0,
        totalRevenue: latestMonthly?.metrics?.arr || 0,
        activeUsersToday: todayMetrics?.metrics?.activeUsers || 0,
        activeAccountsToday: todayMetrics?.metrics?.activeOrganizations || 0,
        revenueToday: todayMetrics?.metrics?.mrr || 0,
        revenueThisMonth: thisMonthMetrics?.metrics?.mrr || 0,
      }
    };
  }

  async getDailyMetrics(startDate?: string, endDate?: string): Promise<DailyMetrics[]> {
    await this.ensureAuthenticated();
    
    const metricsRef = collection(this.firestore, 'analytics_metrics');
    const snapshot = await getDocs(metricsRef);
    let dailyDocs = snapshot.docs
      .filter(doc => doc.id.startsWith('daily_'))
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as DailyMetrics));

    // Filter by date range if provided
    if (startDate) {
      dailyDocs = dailyDocs.filter(d => d.date >= startDate);
    }
    if (endDate) {
      dailyDocs = dailyDocs.filter(d => d.date <= endDate);
    }

    // Sort by date ascending
    return dailyDocs.sort((a, b) => a.date.localeCompare(b.date));
  }

  async getMonthlyMetrics(startMonth?: string, endMonth?: string): Promise<MonthlyMetrics[]> {
    await this.ensureAuthenticated();
    
    const metricsRef = collection(this.firestore, 'analytics_metrics');
    const snapshot = await getDocs(metricsRef);
    let monthlyDocs = snapshot.docs
      .filter(doc => doc.id.startsWith('monthly_'))
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as MonthlyMetrics));

    // Filter by date range if provided
    if (startMonth) {
      monthlyDocs = monthlyDocs.filter(m => m.date >= startMonth);
    }
    if (endMonth) {
      monthlyDocs = monthlyDocs.filter(m => m.date <= endMonth);
    }

    // Sort by date ascending
    return monthlyDocs.sort((a, b) => a.date.localeCompare(b.date));
  }

  async getInvoicePaidEvents(startDate: Date, endDate: Date): Promise<AnalyticsEvent[]> {
    await this.ensureAuthenticated();
    
    const eventsRef = collection(this.firestore, 'analytics_events');
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    
    // Query invoice.paid events in the date range
    const q = query(
      eventsRef,
      where('eventType', '==', 'invoice.paid'),
      where('timestamp', '>=', startTimestamp),
      where('timestamp', '<=', endTimestamp)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as AnalyticsEvent));
  }

  async getPackagePurchasedEvents(startDate: Date, endDate: Date): Promise<AnalyticsEvent[]> {
    await this.ensureAuthenticated();
    
    const eventsRef = collection(this.firestore, 'analytics_events');
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    
    // Query package.purchased events in the date range
    const q = query(
      eventsRef,
      where('eventType', '==', 'package.purchased'),
      where('timestamp', '>=', startTimestamp),
      where('timestamp', '<=', endTimestamp)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as AnalyticsEvent));
  }

  async getCanceledEvents(startDate: Date, endDate: Date): Promise<AnalyticsEvent[]> {
    await this.ensureAuthenticated();
    
    const eventsRef = collection(this.firestore, 'analytics_events');
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    
    // Query organization.canceled events in the date range
    const q = query(
      eventsRef,
      where('eventType', '==', 'organization.canceled'),
      where('timestamp', '>=', startTimestamp),
      where('timestamp', '<=', endTimestamp)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as AnalyticsEvent));
  }

  async getSubscriptionCreatedEvents(startDate: Date, endDate: Date): Promise<AnalyticsEvent[]> {
    await this.ensureAuthenticated();
    
    const eventsRef = collection(this.firestore, 'analytics_events');
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    
    // Query subscription.created events in the date range
    const q = query(
      eventsRef,
      where('eventType', '==', 'subscription.created'),
      where('timestamp', '>=', startTimestamp),
      where('timestamp', '<=', endTimestamp)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as AnalyticsEvent));
  }

  async getTrialExpiredEvents(startDate: Date, endDate: Date): Promise<AnalyticsEvent[]> {
    await this.ensureAuthenticated();

    const eventsRef = collection(this.firestore, 'analytics_events');
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const q = query(
      eventsRef,
      where('eventType', '==', 'trial.expired'),
      where('timestamp', '>=', startTimestamp),
      where('timestamp', '<=', endTimestamp)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as AnalyticsEvent));
  }

  async getTrialConvertedEvents(startDate: Date, endDate: Date): Promise<AnalyticsEvent[]> {
    await this.ensureAuthenticated();

    const eventsRef = collection(this.firestore, 'analytics_events');
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const q = query(
      eventsRef,
      where('eventType', '==', 'trial.converted'),
      where('timestamp', '>=', startTimestamp),
      where('timestamp', '<=', endTimestamp)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as AnalyticsEvent));
  }
}

